import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDownloadBillingConfigImportTemplate, useImportBillingConfigs } from '../../hooks/api/budgetBillingConfig';
import type { ImportBillingConfigsResponse } from '../../services/budgetBillingConfig';
import ImportDialog from './ImportDialog';

interface BillingConfigImportDialogProps {
    open: boolean;
    onClose: () => void;
    versionId: number;
    // CALL-758: the bucket the import writes into (the selected bucket's draft) — surfaced in the
    // description so it's clear the import respects the current bucket selection.
    bucketName?: string;
}

const ACCEPT =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,text/csv,application/vnd.ms-excel,.csv';

const BillingConfigImportDialog = ({ open, onClose, versionId, bucketName }: BillingConfigImportDialogProps) => {
    const { t } = useTranslation('budget');
    const { mutateAsync: importConfigs, isLoading: isImporting } = useImportBillingConfigs(versionId);
    const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } = useDownloadBillingConfigImportTemplate();

    const baseDescription = t('import.billingConfigUploadHint');
    // CALL-758: prepend the target bucket so the user sees the import respects the selected bucket.
    const description = bucketName
        ? `${t('import.rowsIntoBucket', { bucket: bucketName })} ${baseDescription}`
        : baseDescription;

    return (
        <ImportDialog<ImportBillingConfigsResponse>
            open={open}
            onClose={onClose}
            title={t('billingConfig.import')}
            description={description}
            templateAction={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadTemplate('full')}
                        disabled={isDownloadingTemplate}
                    >
                        {t('import.downloadTemplateWithValues')}
                    </Button>
                    <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadTemplate('simple')}
                        disabled={isDownloadingTemplate}
                    >
                        {t('import.downloadSimpleTemplate')}
                    </Button>
                </Box>
            }
            accept={ACCEPT}
            isImporting={isImporting}
            importFile={importConfigs}
            summaryRows={(result) => [
                { label: t('import.rowsParsed'), value: result.rowsParsed },
                { label: t('import.statusCreated'), value: result.createdCount },
                { label: t('import.statusUpdated'), value: result.updatedCount },
                { label: t('import.monthColumns'), value: result.monthColumns.length },
            ]}
            // "Done" means something was actually written; an all-skipped import keeps Import enabled.
            isComplete={(result) => result.createdCount + result.updatedCount > 0}
        />
    );
};

export default BillingConfigImportDialog;
